import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { WidgetState } from './widget.state';
import { computed, inject } from '@angular/core';
import { makeUniqueStringArray } from '../../utility/array.utility';
import { withWidgetCoreFeatures } from './widget.core-features';
import { GridWidgetConfigService } from '../../services/grid-widget-config.service';
import { WidgetHelperService } from '../../services/widget/widget-helper.service';

export function withGridMethods() {
  return signalStoreFeature(
    {
      state: type<WidgetState>(),
    },
    withWidgetCoreFeatures(),
    withComputed(
      ({ entities }, widgetHelper = inject(WidgetHelperService)) => ({
        gridTypes: computed(() => {
          return entities()
            .map(item => {
              return item.widget.dataLayerType;
            })
            .filter(item => !!item);
        }),
        gridLayers: computed(() => {
          return makeUniqueStringArray(
            entities()
              .map(item => {
                return item.widget.dataLayer;
              })
              .filter(item => !!item)
          );
        }),
        gridIndicators: computed(() => {
          return entities()
            .flatMap(item => item.widget.indicators || [])
            .reduce(makeUniqueStringArray, []);
        }),
        hasAdderWidget: computed(() => {
          for (const idx in entities()) {
            const item = entities()[idx];
            if (item.type === 'adder') return true;
          }
          return false;
        }),
        hasDataWidget: computed(() => {
          for (const idx in entities()) {
            const item = entities()[idx];
            if (widgetHelper.isDataWidget(item.type)) return true;
          }
          return false;
        }),
        /**
         * Whether the grid has no real widgets yet - only (at most) the
         * "adder" tile - i.e. the blank Werkstatt starting point where
         * template cards should still be offered.
         */
        isEmpty: computed(() => {
          //return entities().filter(item => item.type !== 'adder').length === 0;
          return entities().length === 0;
        }),
      })
    ),
    withMethods((store, configService = inject(GridWidgetConfigService)) => {
      const gridMethods = {
        configPosition() {
          const selected = store.selected();
          if (selected && selected.x && selected.x > 3) {
            return 'left';
          } else {
            return 'right';
          }
        },
        openConfig(id: string, forceFirstStep = false, poiFastSwitch = false) {
          const item = store.get(id);
          if (!item) return;
          let activeStep = 1;

          if (
            !forceFirstStep &&
            item?.widget?.dataLayer &&
            item?.widget?.indicators.length > 0
          ) {
            activeStep = configService.numberOfSteps(item.type);
          }
          const widgetBackup = structuredClone(item?.widget);
          const itemBackup = configService.transformWidgetToSavableItem(item);

          patchState(store, {
            activeConfig: activeStep,
            selectedId: id,
            rerenderId: '',
            poiFastSwitch,
            prevConfig:
              itemBackup && widgetBackup
                ? { ...itemBackup, ...{ widget: widgetBackup } }
                : null,
          });
        },
        closeConfig() {
          patchState(store, {
            activeConfig: false,
            selectedId: '',
            rerenderId: '',
            poiFastSwitch: false,
            prevConfig: null,
          });
        },
      };
      return gridMethods;
    })
  );
}
